import { auth } from './firebase';

export const ACTIVE_ORG_STORAGE_KEY = 'aspectly_active_org_id';

interface ApiOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
}

/** Resolve active org id from argument or ``localStorage`` (browser only). */
export function resolveActiveOrganizationId(organizationId?: number | string): string | null {
  if (organizationId !== undefined) return String(organizationId);
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
}

/** Path prefix ``/organizations/{activeOrg}/…`` or throws if none selected. */
export function orgApiPath(suffix: string): string {
  const id = resolveActiveOrganizationId();
  if (!id) throw new Error('No active organization');
  const p = suffix.startsWith('/') ? suffix : `/${suffix}`;
  return `/organizations/${id}${p}`;
}

function formatApiErrorDetail(detail: unknown): string {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg);
        }
        return JSON.stringify(item);
      })
      .filter(Boolean)
      .join('; ');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

/**
 * Authenticated fetch wrapper that attaches the Firebase ID token.
 * Org scope must appear in ``path`` (e.g. ``/organizations/{id}/campaigns``).
 */
export async function apiClient<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`/api/v1${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const contentType = res.headers.get('content-type') ?? '';
    const text = await res.text();
    let message = '';
    if (contentType.includes('application/json') && text) {
      try {
        const body = JSON.parse(text) as { detail?: unknown };
        message = formatApiErrorDetail(body.detail);
      } catch {
        message = text.trim();
      }
    } else {
      message = text.trim();
    }
    throw new Error(message || `API error ${res.status}: ${res.statusText}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }

  return (await res.text()) as T;
}

/**
 * Authenticated GET returning raw bytes (e.g. asset media).
 */
export async function authFetchBlob(path: string): Promise<Blob> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  const res = await fetch(`/api/v1${path}`, {
    method: 'GET',
    headers,
  });

  if (!res.ok) {
    const text = await res.text();
    let message = text.trim();
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('application/json') && text) {
      try {
        const body = JSON.parse(text) as { detail?: unknown };
        message = formatApiErrorDetail(body.detail) || message;
      } catch {
        /* keep message */
      }
    }
    throw new Error(message || `API error ${res.status}: ${res.statusText}`);
  }

  return res.blob();
}
